import quickFetch from 'quick-fetch';
import Raven from 'raven-js';
import identity from 'transmute/identity';
// @ts-expect-error No types
import { createAction } from 'flux-actions';
import { createRpcClientV2 } from 'rpc-client-utils';
import * as PresentationsV1PublicApi from 'documents-lib/js/api/PresentationsV1PublicApi';
import * as PresentationsV2PublicApi from 'documents-lib/js/api/PresentationsV2PublicApi';
import PresentationRecord from 'documents-lib/js/records/PresentationRecord';
import LegalConsentRecord from 'documents-lib/js/records/LegalConsentRecord';
import apiClient from 'documents-lib/js/utils/SPMRedirectNoAuthApiClient';
import { PRESENTATION_ACTION_TYPES, GDPR_ACTION_TYPES } from 'documents-lib/js/constants/ActionTypes';
import { PUBLIC_PRESENTATION_NOT_AVAILABLE_ERROR, PUBLIC_SHARED_LINK_NOT_FOUND, PUBLIC_SHARED_LINK_DOCUMENT_NOT_FOUND, PUBLIC_SHARED_LINK_EXPIRED, PUBLIC_SHARED_LINK_TEMPORARILY_UNAVAILABLE } from 'documents-lib/js/constants/ErrorTypes';
import { ensureGdprLang } from '../utils/ensureGdprLang';
import { initTracker } from 'documents-lib/js/tracking/tracker';
import { getPublicSharedLinkRpc } from '../../__generated__/chirp/com/hubspot/sales/documents/rpc/SalesDocumentsPublicSharedLinkService';
const {
  PUBLIC_PRESENTATION_FETCH_SUCCEEDED,
  PUBLIC_PRESENTATION_FETCH_FAILED,
  PUBLIC_SHARED_LINK_FETCH_SUCCEEDED,
  PUBLIC_PRESENTATION_NOT_FOUND,
  PUBLIC_PRESENTATION_NOT_AVAILABLE,
  PUBLIC_PRESENTATION_INCORRECT_HUBLET
} = PRESENTATION_ACTION_TYPES;
const {
  PUBLIC_DOCUMENTS_LEGAL_CONSENT_FETCH_FAILED,
  PUBLIC_DOCUMENTS_LEGAL_CONSENT_FETCH_SUCCEEDED,
  PUBLIC_DOCUMENTS_GDPR_LANG_LOADING,
  PUBLIC_DOCUMENTS_LEGAL_CONSENT_FETCH_CHIRP_SUCCEEDED
} = GDPR_ACTION_TYPES;
const fetchWithEarlyRequestHook = (requestName, fallbackFetch, transformSuccess, portalMigrationCallback) => {
  const earlyRequest = quickFetch.getRequestStateByName(requestName);
  if (earlyRequest) {
    return new Promise((resolve, reject) => {
      earlyRequest.whenFinished((...args) => {
        resolve(transformSuccess(...args));
      });
      earlyRequest.onError(xhr => {
        if (xhr.status === 488) {
          // 488 can occur after a portal has done Seamless Portal Migration.
          // Perform the API fetch again with a client that includes
          // the SPM middlewares.
          if (portalMigrationCallback) portalMigrationCallback();
          fallbackFetch().then(result => resolve(result)).catch(error => reject(error));
          return;
        }
        const error = new Error(`API request has failed with status code ${xhr.status}`, {
          cause: {
            status: xhr.status,
            statusText: xhr.statusText,
            data: xhr.responseText,
            responseUrl: xhr.responseURL,
            responseText: xhr.responseText
          }
        });
        return reject(error);
      });
    });
  }
  return fallbackFetch();
};
export const fetchPresentationSuccess = createAction(PUBLIC_PRESENTATION_FETCH_SUCCEEDED, identity);
export const fetchSharedLinkSuccess = createAction(PUBLIC_SHARED_LINK_FETCH_SUCCEEDED, identity);
const fetchPresentationFailed = createAction(PUBLIC_PRESENTATION_FETCH_FAILED, identity);
const fetchPresentationNotFound = createAction(PUBLIC_PRESENTATION_NOT_FOUND, identity);
const fetchPresentationNotAvailable = createAction(PUBLIC_PRESENTATION_NOT_AVAILABLE, identity);
const fetchPresentationIncorrectHublet = createAction(PUBLIC_PRESENTATION_INCORRECT_HUBLET, identity);
export const fetchDocumentsLegalConsentSuccess = createAction(PUBLIC_DOCUMENTS_LEGAL_CONSENT_FETCH_SUCCEEDED, identity);
export const fetchDocumentsLegalConsentCHIRPSuccess = createAction(PUBLIC_DOCUMENTS_LEGAL_CONSENT_FETCH_CHIRP_SUCCEEDED, payload => payload);
const fetchDocumentsLegalConsentFailed = createAction(PUBLIC_DOCUMENTS_LEGAL_CONSENT_FETCH_FAILED, identity);
export const documentsGDPRLangLoading = createAction(PUBLIC_DOCUMENTS_GDPR_LANG_LOADING, identity);
let _pendingPresentationFetch = null;
export const fetchSharedLink = (sharedLinkId, accessId) => dispatch => {
  if (_pendingPresentationFetch !== null) {
    return;
  }
  const rpcClient = createRpcClientV2({
    hubHttpClient: apiClient
  });
  _pendingPresentationFetch = rpcClient.call(getPublicSharedLinkRpc, {
    request: {
      sharedLinkId,
      accessId
    }
  }).then(response => {
    initTracker({
      isPublic: true
    });
    dispatch(fetchSharedLinkSuccess(response));
  }).catch(err => {
    var _err$error, _err$error2;
    const errorReason = err === null || err === void 0 || (_err$error = err.error) === null || _err$error === void 0 ? void 0 : _err$error.reason;
    const errorMessage = err === null || err === void 0 || (_err$error2 = err.error) === null || _err$error2 === void 0 ? void 0 : _err$error2.chirpErrorMessage;
    if (errorReason === PUBLIC_SHARED_LINK_NOT_FOUND || errorReason === PUBLIC_SHARED_LINK_DOCUMENT_NOT_FOUND) {
      dispatch(fetchPresentationNotFound(err));
      return;
    }
    if (errorReason === PUBLIC_SHARED_LINK_EXPIRED || errorReason === PUBLIC_SHARED_LINK_TEMPORARILY_UNAVAILABLE) {
      dispatch(fetchPresentationNotAvailable(err));
      return;
    }
    dispatch(fetchPresentationFailed(err));
    Raven.captureException(err, {
      extra: {
        errorReason,
        context: errorMessage
      }
    });
  }).finally(() => {
    _pendingPresentationFetch = null;
  });
};
export const fetchPresentation = (presentationId, accessId) => dispatch => {
  if (_pendingPresentationFetch !== null) {
    return;
  }
  _pendingPresentationFetch = fetchWithEarlyRequestHook('public-presentation', () => PresentationsV2PublicApi.fetchPresentation(presentationId, accessId), presentation => PresentationRecord.create(presentation), () => dispatch(fetchPresentationIncorrectHublet())).then(presentation => {
    initTracker({
      isPublic: true
    });
    dispatch(fetchPresentationSuccess(presentation));
  }).catch(err => {
    // Try to handle known edge cases and exceptions
    const data = err.data || err.cause.data;
    if (data) {
      try {
        const responseJSON = JSON.parse(data);

        /* This is a handling of 488 errors after portal migration.
        In some cases the app would be redirected before reaching this statement,
        for others we need to prevent the catch block logic from tracking false errors. */
        if (responseJSON.status === 'error' && responseJSON.correctHublet) return;
        if (responseJSON.subCategory === PUBLIC_PRESENTATION_NOT_AVAILABLE_ERROR) {
          dispatch(fetchPresentationNotAvailable(responseJSON));
          return;
        }
        if (responseJSON.message === 'Document not found') {
          dispatch(fetchPresentationNotFound(err));
          return;
        }
      } catch (e) {
        // This will catch JSON parsing and error handling exceptions as opposed to API errors
        Raven.captureException(e, {
          extra: {
            originalError: Object.assign({}, err),
            context: 'Error handling exception'
          }
        });
      }
    }

    // This will catch API errors that were unhandled by the conditions above
    dispatch(fetchPresentationFailed(err));
    Raven.captureException(err, {
      extra: {
        cause: err.cause,
        context: 'Fetch presentation error'
      }
    });
  }).finally(() => {
    _pendingPresentationFetch = null;
  });
};
export const fetchDownloadLink = (presentationId, accessId) => () => PresentationsV2PublicApi.fetchPresentationDownloadUrl(presentationId, accessId).then(({
  url
}) => url).catch(error => console.error(error));
export const fetchLegalConsent = (presentationId, accessId) => dispatch => fetchWithEarlyRequestHook('public-legal-consent', () => PresentationsV1PublicApi.fetchLegalConsent(presentationId, accessId).catch(err => {
  throw err;
}), ensureGdprLang).then(gdprSettings => {
  dispatch(fetchDocumentsLegalConsentSuccess(LegalConsentRecord.fromJS(gdprSettings)));
}).catch(err => {
  // Try to handle known edge cases and exceptions
  const data = err.data || err.cause.data;
  if (data) {
    try {
      const responseJSON = JSON.parse(data);

      /* This is a handling of 488 errors after portal migration.
      In some cases the app would be redirected before reaching this statement,
      for others we need to prevent the catch block logic from tracking false errors. */
      if (responseJSON.status === 'error' && responseJSON.correctHublet) return;
      if (responseJSON && responseJSON.subCategory === PUBLIC_PRESENTATION_NOT_AVAILABLE_ERROR) {
        dispatch(fetchPresentationNotAvailable(responseJSON));
        return;
      }
      if (responseJSON.message && (responseJSON.message.startsWith('No Deck found') || responseJSON.message.startsWith('Document not found'))) {
        dispatch(fetchPresentationNotFound(err));
        dispatch(fetchDocumentsLegalConsentFailed(err));
        return;
      }
    } catch (e) {
      // This will catch JSON parsing and error handling exceptions as opposed to API errors
      Raven.captureException(e, {
        extra: {
          originalError: Object.assign({}, err),
          context: 'Error handling exception'
        }
      });
    }
  }

  // This will catch API errors that were unhandled by the conditions above
  dispatch(fetchDocumentsLegalConsentFailed(err));
  Raven.captureException(err, {
    extra: {
      cause: err.cause,
      context: 'Fetch legal consent error'
    }
  });
});